import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/use-auth'
import { useOrgBranding } from '@/hooks/use-org-branding'
import { authService } from '@/services/auth.service'
import { useAuthStore } from '@/stores/auth.store'
import type { ApiError, OTPResponse } from '@/types'
import { ArrowLeft, Loader2 } from 'lucide-react'
import type React from 'react'
import type { ClipboardEvent, KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export function PortalLoginPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { slug, orgName, logoUrl, isLoading: brandingLoading } = useOrgBranding()

  const [step, setStep] = useState<'identifier' | 'otp'>('identifier')
  const [identifier, setIdentifier] = useState('')
  const [channel, setChannel] = useState<'email' | 'sms'>('email')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [otpAttempts, setOtpAttempts] = useState(0)
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, authLoading, navigate])

  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100)
    }
  }, [step])

  const handleIdentifierSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = identifier.trim()
    if (!trimmed || trimmed.length < 5) return

    setIsLoading(true)
    setError(null)

    try {
      const response: OTPResponse = await authService.requestOTP(
        trimmed,
        false,
        true,
        slug || undefined,
      )
      setChannel(response.channel)
      setStep('otp')
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to send verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const verifyOTP = async (code: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authService.verifyOTP(identifier.trim(), code, slug || undefined)
      setAuth(response.user, response.access_token)
      navigate('/dashboard')
    } catch (err) {
      const apiError = err as ApiError
      const newAttempts = otpAttempts + 1
      setOtpAttempts(newAttempts)
      if (newAttempts >= 2) {
        setError('Too many failed attempts. Please request a new code.')
      } else {
        setError(apiError.message || 'Invalid code. You have 1 attempt remaining.')
      }
      setOtpDigits(['', '', '', '', '', ''])
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    const digitsOnly = value.replace(/\D/g, '')
    if (digitsOnly.length > 1) {
      const newDigits = [...otpDigits]
      for (let i = 0; i < digitsOnly.length && i < 6; i++) {
        newDigits[i] = digitsOnly.charAt(i)
      }
      setOtpDigits(newDigits)
      if (digitsOnly.length >= 6) {
        verifyOTP(digitsOnly.slice(0, 6))
      } else {
        otpInputRefs.current[digitsOnly.length]?.focus()
      }
      return
    }
    if (digitsOnly.length === 0 && value.length > 0) return
    const newDigits = [...otpDigits]
    newDigits[index] = digitsOnly
    setOtpDigits(newDigits)
    if (digitsOnly && index < 5) otpInputRefs.current[index + 1]?.focus()
    if (digitsOnly && index === 5) {
      const code = newDigits.join('')
      if (code.length === 6) verifyOTP(code)
    }
  }

  const handleOtpKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e: ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 0) return
    const newDigits = [...otpDigits]
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newDigits[i] = pastedData.charAt(i)
    }
    setOtpDigits(newDigits)
    const fullCode = newDigits.join('')
    if (fullCode.length === 6 && !newDigits.includes('')) {
      verifyOTP(fullCode)
    }
  }

  const handleResendCode = async () => {
    setIsLoading(true)
    setError(null)
    try {
      await authService.requestOTP(identifier.trim(), false, true, slug || undefined)
      setOtpDigits(['', '', '', '', '', ''])
      setOtpAttempts(0)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to resend code')
    } finally {
      setIsLoading(false)
    }
  }

  if (authLoading || brandingLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container flex min-h-screen items-center justify-center py-10">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="space-y-4 pb-8">
            <div className="flex justify-center">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={orgName || ''}
                  className="h-12 w-auto max-w-[200px] object-contain"
                />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <span className="text-lg font-bold text-blue-600">
                    {orgName?.charAt(0)?.toUpperCase() || 'P'}
                  </span>
                </div>
              )}
            </div>
            <CardTitle className="text-center text-2xl">
              {step === 'otp'
                ? channel === 'email'
                  ? 'Check Your Email'
                  : 'Check Your Phone'
                : 'Sign In'}
            </CardTitle>
            <CardDescription className="text-center">
              {step === 'otp'
                ? `We sent a 6-digit code to ${identifier}`
                : `Sign in to your ${orgName || 'Pelgo'} portal`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'identifier' ? (
              <form onSubmit={handleIdentifierSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier">Email or Phone Number</Label>
                  <Input
                    id="identifier"
                    type="text"
                    placeholder="you@example.com or +1234567890"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {error && (
                  <div className="rounded-md bg-destructive/10 p-3">
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={isLoading || !identifier.trim()}>
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending code...
                    </>
                  ) : (
                    'Send verification code'
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="block text-center">Verification Code</Label>
                  <div className="flex justify-center gap-2">
                    {otpDigits.map((digit, index) => (
                      <Input
                        // biome-ignore lint/suspicious/noArrayIndexKey: OTP array is fixed size and order never changes
                        key={index}
                        ref={(el) => {
                          otpInputRefs.current[index] = el
                        }}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={(e) => handleOtpPaste(e)}
                        disabled={isLoading || otpAttempts >= 2}
                        className="h-14 w-12 text-center text-2xl font-semibold"
                      />
                    ))}
                  </div>
                  {isLoading && (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Verifying...
                    </div>
                  )}
                </div>
                {error && (
                  <div className="rounded-md bg-destructive/10 p-3">
                    <p className="text-center text-sm text-destructive">{error}</p>
                  </div>
                )}
                <div className="flex flex-col gap-2 pt-2">
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={handleResendCode}
                    disabled={isLoading}
                  >
                    Resend code
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setStep('identifier')
                      setError(null)
                      setOtpDigits(['', '', '', '', '', ''])
                      setOtpAttempts(0)
                    }}
                    disabled={isLoading}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Use different email or phone
                  </Button>
                </div>
              </div>
            )}
            <div className="mt-6 pt-4 border-t">
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span>256-bit encrypted. Your data is private and secure.</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
